import { useState } from "react";
import { CalendarIcon } from "lucide-react";
import { FieldPath, PathValue, UseFormReturn } from "react-hook-form";

import { OptionalLabel } from "@/components/OptionalLabel";
import { Row } from "@/components/Row";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";

export const FormDateInput = <T extends Record<string, unknown>>({
  className,
  disabled,
  fieldName,
  form,
  label,
  optional,
}: {
  className?: string;
  disabled?: boolean;
  fieldName: FieldPath<T>;
  form: UseFormReturn<T>;
  label: string;
  optional?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>();

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => {
        return (
          <FormItem className={cn("text-slate-700", className)}>
            <FormLabel>
              {label}
              {optional && <OptionalLabel className="ml-1.5" />}
            </FormLabel>
            <FormControl>
              <Row className="items-center gap-2">
                <Input
                  {...field}
                  disabled={disabled}
                  value={field.value as string}
                />
                <Popover open={open} onOpenChange={setOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      disabled={disabled}
                      variant="ghost"
                      className="border border-transparent focus:border-slate-300"
                    >
                      <CalendarIcon />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(date) => {
                        setDate(date);
                        const formattedDate = dates.formatIsoDateForUser(
                          date?.toISOString(),
                        );

                        // shouldValidate is a nice to have so any error messages are cleared on date selection
                        form.setValue(
                          fieldName,
                          formattedDate as PathValue<T, FieldPath<T>>,
                          {
                            shouldValidate: true,
                          },
                        );
                        setOpen(false);
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </Row>
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
